/**
 * The bulk step of New from photos (021 R14): the picked photos arranged into
 * items, each item to become one draft. A pure value — every change returns a
 * new selection — so the screen holds it in state and tests need no UI.
 *
 * Invariants: at most `MAX_BULK_PHOTOS` photos, each in exactly one item, no
 * empty item, and photos within an item in pick order. Items are never
 * reordered; there is no automatic grouping.
 */

/** One selection, like the platform pickers' cap (FR-002). */
export const MAX_BULK_PHOTOS = 20;

/** Marked when Create is tapped; Drafts measures from it to being shown (NFR-001, R12). */
export const BULK_CREATE_MARK = 'bulk.create.start';

export type PhotoSize = { width: number; height: number };

export type BulkPhoto = {
  id: string;
  file: File;
  /** Known once the photo is copied into the page and read; null while copying. */
  size: PhotoSize | null;
  /** The file couldn't be read as a photo; it stays until removed. */
  failed: boolean;
};

export type BulkItem = { id: string; photos: BulkPhoto[] };

/** What Undo reverses; removing a photo isn't one of them. */
export type UndoableChange = 'group' | 'merge' | 'takeOut';

/** Items as photo ids. An item's id is its first photo's. */
type Layout = readonly (readonly string[])[];

type Photos = ReadonlyMap<string, BulkPhoto & { order: number }>;

export class BulkSelection {
  private constructor(
    private readonly photos: Photos,
    private readonly layout: Layout,
    private readonly before: { layout: Layout; change: UndoableChange } | null,
  ) {}

  /** One item per photo, in pick order; photos past the 20th are left out. */
  static start(picked: { id: string; file: File }[]): BulkSelection {
    const kept = picked.slice(0, MAX_BULK_PHOTOS);
    const photos = new Map(
      kept.map(({ id, file }, order) => [id, { id, file, size: null, failed: false, order }]),
    );
    return new BulkSelection(
      photos,
      kept.map(({ id }) => [id]),
      null,
    );
  }

  // ── Reading ───────────────────────────────────────────────────────────

  get items(): BulkItem[] {
    return this.layout.map((ids) => ({ id: ids[0], photos: ids.map((id) => this.photo(id)) }));
  }

  get photoCount(): number {
    return this.photos.size;
  }

  get canUndo(): boolean {
    return this.before !== null;
  }

  get lastChange(): UndoableChange | null {
    return this.before?.change ?? null;
  }

  /** Items whose photos are all copied: what "Create N Drafts" makes (FR-006). */
  readyItems(): BulkItem[] {
    return this.items.filter((item) => item.photos.every((p) => p.size !== null && !p.failed));
  }

  get createCount(): number {
    return this.readyItems().length;
  }

  /** Items left out of Create because a photo is still being copied. */
  get copyingCount(): number {
    return this.items.filter(
      (item) => !item.photos.some((p) => p.failed) && item.photos.some((p) => p.size === null),
    ).length;
  }

  /** Items left out of Create because a photo couldn't be read. */
  get failedCount(): number {
    return this.items.filter((item) => item.photos.some((p) => p.failed)).length;
  }

  /** "Item 3 of 11, 2 photos" (NFR-005). */
  itemLabel(index: number): string {
    const n = this.layout[index].length;
    return `Item ${index + 1} of ${this.layout.length}, ${n} ${n === 1 ? 'photo' : 'photos'}`;
  }

  // ── Arranging (undoable) ──────────────────────────────────────────────

  /**
   * The selected photos become one item at the position of the first
   * selected photo, in pick order. Needs two or more photos.
   */
  group(photoIds: Iterable<string>): BulkSelection {
    return this.arranged(this.grouped(photoIds), 'group');
  }

  /** Join an item to the one before it; any item but the first. */
  mergeWithPrevious(itemId: string): BulkSelection {
    const index = this.layout.findIndex((ids) => ids[0] === itemId);
    if (index < 1) return this;
    return this.arranged(this.grouped([...this.layout[index - 1], ...this.layout[index]]), 'merge');
  }

  /** A photo of a multi-photo item becomes its own item, right after its former item. */
  takeOut(photoId: string): BulkSelection {
    const index = this.layout.findIndex((ids) => ids.includes(photoId));
    if (index === -1 || this.layout[index].length < 2) return this;
    const rest = this.layout[index].filter((id) => id !== photoId);
    return this.arranged(
      [...this.layout.slice(0, index), rest, [photoId], ...this.layout.slice(index + 1)],
      'takeOut',
    );
  }

  /** Reverse the last group, merge or take-out (one level). */
  undo(): BulkSelection {
    if (!this.before) return this;
    return new BulkSelection(this.photos, this.before.layout, null);
  }

  // ── Not undoable ──────────────────────────────────────────────────────

  /** Drop a photo, and its item if it was the item's only photo. Clears Undo. */
  removePhoto(photoId: string): BulkSelection {
    if (!this.photos.has(photoId)) return this;
    const photos = new Map(this.photos);
    photos.delete(photoId);
    return new BulkSelection(photos, this.without([photoId]), null);
  }

  /** The created items leave; the rest stay to finish copying. Clears Undo. */
  withoutItems(itemIds: string[]): BulkSelection {
    const leaving = this.layout.filter((ids) => itemIds.includes(ids[0])).flat();
    const photos = new Map(this.photos);
    leaving.forEach((id) => photos.delete(id));
    return new BulkSelection(photos, this.without(leaving), null);
  }

  /** The photo is in the page and its size is known. */
  copied(photoId: string, size: PhotoSize): BulkSelection {
    return this.withPhoto(photoId, { size, failed: false });
  }

  /** The photo couldn't be read. */
  failed(photoId: string): BulkSelection {
    return this.withPhoto(photoId, { failed: true });
  }

  // ── Machinery ─────────────────────────────────────────────────────────

  private photo(id: string): BulkPhoto {
    const { order: _order, ...photo } = this.photos.get(id)!;
    return photo;
  }

  private byPickOrder = (a: string, b: string) =>
    this.photos.get(a)!.order - this.photos.get(b)!.order;

  private grouped(photoIds: Iterable<string>): Layout | null {
    const selected = [...new Set(photoIds)].filter((id) => this.photos.has(id));
    if (selected.length < 2) return null;
    selected.sort(this.byPickOrder);
    const first = selected[0];
    const result: string[][] = [];
    for (const ids of this.layout) {
      const rest = ids.filter((id) => !selected.includes(id));
      if (!ids.includes(first)) {
        if (rest.length > 0) result.push(rest);
        continue;
      }
      // What's left of the first photo's item stays beside the new item,
      // on the side its own first photo was picked.
      if (rest.length > 0 && this.byPickOrder(rest[0], first) < 0) result.push(rest, selected);
      else result.push(selected, ...(rest.length > 0 ? [rest] : []));
    }
    return result;
  }

  private arranged(layout: Layout | null, change: UndoableChange): BulkSelection {
    if (!layout) return this;
    return new BulkSelection(this.photos, layout, { layout: this.layout, change });
  }

  private without(photoIds: string[]): Layout {
    return this.layout
      .map((ids) => ids.filter((id) => !photoIds.includes(id)))
      .filter((ids) => ids.length > 0);
  }

  private withPhoto(photoId: string, changes: Partial<BulkPhoto>): BulkSelection {
    const photo = this.photos.get(photoId);
    if (!photo) return this;
    const photos = new Map(this.photos);
    photos.set(photoId, { ...photo, ...changes });
    return new BulkSelection(photos, this.layout, this.before);
  }
}
